import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search, ArrowRight, Star, Loader2, X, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import { usePriceTicks, useSubscribePrices } from "../context/LiveStreamContext";
import { StalenessBadge } from "../components/StalenessBadge";

// Curated one-tap chips — the tickers people search for most. Kept small so
// the page stays focused on lookup, not browsing.
const POPULAR_STOCKS = ["AAPL", "TSLA", "NVDA", "SPY", "QQQ", "MSFT", "AMZN", "META", "AMD", "GOOGL", "NFLX", "GLD"];
const POPULAR_CRYPTO = ["bitcoin", "ethereum", "solana", "ripple", "cardano", "dogecoin", "chainlink", "avalanche-2"];

const RECENT_KEY = "aanar_recent_lookups";

export default function Markets() {
  const [q, setQ] = useState("");
  const [assetType, setAssetType] = useState("stock");
  const [preview, setPreview] = useState(null);        // { name, price, change_pct, ... } or null
  const [previewErr, setPreviewErr] = useState(null);  // "not found" or null
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      if (Array.isArray(raw)) setRecent(raw.slice(0, 8));
    } catch { /* noop */ }
  }, []);

  const looksLikeTicker = (val) => /^[a-zA-Z0-9.\-]{1,16}$/.test(val.trim());

  // Live-fetch a preview as the user types (debounced 450ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim() || !looksLikeTicker(q)) {
      setPreview(null); setPreviewErr(null); setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true); setPreviewErr(null);
      const sym = q.trim();
      const finalSym = assetType === "crypto" ? sym.toLowerCase() : sym.toUpperCase();
      try {
        const { data } = await api.get(`/assets/${assetType}/${finalSym}`);
        if (data && (data.price != null || data.name)) {
          setPreview({ ...data, _symbol: finalSym });
          setPreviewErr(null);
        } else {
          setPreview(null); setPreviewErr("not-found");
        }
      } catch {
        setPreview(null); setPreviewErr("not-found");
      }
      setLoading(false);
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [q, assetType]);

  const rememberRecent = (sym, type) => {
    const key = `${type}:${sym}`;
    const next = [{ symbol: sym, type }, ...recent.filter((r) => `${r.type}:${r.symbol}` !== key)].slice(0, 8);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
  };

  const openAsset = (sym, type) => {
    rememberRecent(sym, type);
    navigate(`/asset/${type}/${sym}`);
  };

  const addToWatchlist = async (sym, type, verified) => {
    setAdding(true);
    try {
      await api.post("/watchlist", { symbol: sym, asset_type: type });
      toast.success(`Added ${sym.toUpperCase()} to watchlist`, {
        description: verified
          ? `${verified.name || sym} · ${fmtMoney(verified.price)}`
          : "Price will populate on the next refresh.",
      });
      rememberRecent(sym, type);
      setQ("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add");
    }
    setAdding(false);
  };

  const clearRecent = () => {
    setRecent([]);
    try { localStorage.removeItem(RECENT_KEY); } catch { /* noop */ }
  };

  const popular = assetType === "crypto" ? POPULAR_CRYPTO : POPULAR_STOCKS;

  // Live ticks: subscribe to the popular chips + preview symbol so users see
  // prices update every 10s without navigating away.
  const live = usePriceTicks();
  const subSymbols = useMemo(() => {
    const list = popular.map((s) => ({ symbol: s, asset_type: assetType }));
    if (preview?._symbol) list.push({ symbol: preview._symbol, asset_type: assetType });
    return list;
  }, [popular, assetType, preview]);
  useSubscribePrices(subSymbols);
  // Overlay the tick onto the preview card so its price scoreboard updates live.
  const previewLive = preview && live[preview._symbol];
  const previewShown = previewLive
    ? { ...preview, price: previewLive.p, change_pct: previewLive.ch }
    : preview;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="markets-page">
      <div className="pt-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Look up any asset</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Markets</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Type any US-listed ticker or supported crypto — Aanar fetches the live price
          and lets you watch, chart, or trade it.
        </p>
      </div>

      {/* Type selector + search input */}
      <Card className="p-4 rounded-2xl border-border">
        <Tabs value={assetType} onValueChange={setAssetType} className="mb-3">
          <TabsList data-testid="market-tabs" className="w-full grid grid-cols-2">
            <TabsTrigger value="stock" data-testid="tab-stocks">Stock / ETF</TabsTrigger>
            <TabsTrigger value="crypto" data-testid="tab-crypto">Crypto</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={assetType === "crypto" ? "e.g. bitcoin, ethereum, solana" : "e.g. SHAZ, TSLA, BRK.B"}
            autoFocus
            autoComplete="off"
            maxLength={16}
            className="pl-9 pr-10 h-11 text-sm font-mono-tab uppercase rounded-full bg-secondary/30 border-border/60"
            data-testid="market-search"
          />
          {q && (
            <button onClick={() => setQ("")} data-testid="market-search-clear"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </Card>

      {/* Live preview card */}
      {q.trim() && (
        <Card className="p-5 rounded-2xl border-emerald-500/40 bg-emerald-500/[0.04]" data-testid="lookup-preview">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="lookup-loading">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-400" /> Fetching {q.toUpperCase()}…
            </div>
          )}
          {!loading && previewShown && (
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-1">
                  {previewShown.image ? (
                    <img src={previewShown.image} alt={previewShown._symbol} className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-xs font-bold text-emerald-300">
                      {(previewShown.ticker || previewShown._symbol).toString().slice(0, 3).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display text-xl font-bold">{(previewShown.ticker || previewShown._symbol).toString().toUpperCase()}</div>
                    <div className="text-xs text-muted-foreground truncate">{previewShown.name || assetType}</div>
                  </div>
                  {previewLive && (
                    <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--gain))] flex items-center gap-1"
                          data-testid="lookup-live-indicator">
                      <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gain))] animate-pulse" /> live
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-3 font-mono-tab mt-2">
                  <div className="text-2xl font-bold" data-testid="lookup-price">{fmtMoney(previewShown.price)}</div>
                  <div className={`text-sm ${previewShown.change_pct >= 0 ? "gain" : "loss"}`}>
                    {fmtPct(previewShown.change_pct)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap w-full sm:w-auto">
                <button onClick={() => addToWatchlist(previewShown._symbol, assetType, previewShown)}
                        disabled={adding}
                        data-testid="lookup-add-watch"
                        className="flex-1 sm:flex-none h-10 px-4 rounded-full bg-secondary/70 border border-border hover:bg-secondary text-xs font-semibold flex items-center justify-center gap-1.5">
                  <Star className="w-3.5 h-3.5" /> {adding ? "Adding…" : "Watchlist"}
                </button>
                <button onClick={() => openAsset(previewShown._symbol, assetType)}
                        data-testid="lookup-open"
                        className="flex-1 sm:flex-none h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-bold flex items-center justify-center gap-1.5">
                  Open <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
          {!loading && !previewShown && previewErr === "not-found" && (
            <div className="flex items-start justify-between gap-3 flex-wrap" data-testid="lookup-not-found">
              <div>
                <div className="font-display text-lg font-semibold">"{q.toUpperCase()}" not found as {assetType}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {assetType === "stock"
                    ? "Try the exact IBKR/yfinance ticker, or switch to Crypto if this is a coin."
                    : "Use the coin's slug (bitcoin, ethereum, solana), or switch to Stock."}
                </div>
              </div>
              <button
                onClick={() => addToWatchlist(assetType === "crypto" ? q.trim().toLowerCase() : q.trim().toUpperCase(), assetType, null)}
                disabled={adding}
                data-testid="lookup-add-anyway"
                className="h-9 px-3 rounded-full bg-secondary/70 border border-border text-xs font-medium">
                Add anyway
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Popular one-tap chips */}
      {!q.trim() && (
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            <Sparkles className="w-3 h-3" /> Popular {assetType === "crypto" ? "crypto" : "tickers"}
            <StalenessBadge symbols={popular.map((s) => ({ symbol: s }))} testId="markets-freshness" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="market-popular-chips">
            {popular.map((sym) => {
              const tick = live[sym];
              return (
                <button key={sym} onClick={() => setQ(sym)}
                        data-testid={`market-chip-${sym}`}
                        className="px-3 py-1.5 rounded-full text-xs font-mono-tab uppercase bg-secondary/50 border border-border/50 hover:bg-secondary hover:border-border transition-colors flex items-center gap-2">
                  <span>{sym}</span>
                  {tick && tick.p != null && (
                    <span className={`text-[10px] ${tick.ch >= 0 ? "gain" : "loss"}`}
                          data-testid={`market-chip-tick-${sym}`}>
                      {tick.ch >= 0 ? "+" : ""}{Number(tick.ch || 0).toFixed(2)}%
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recently viewed */}
      {!q.trim() && recent.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <Clock className="w-3 h-3" /> Recently viewed
            </div>
            <button onClick={clearRecent} className="text-[10px] text-muted-foreground hover:text-foreground underline"
                    data-testid="market-recent-clear">Clear</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="market-recent-chips">
            {recent.map((r) => (
              <button key={`${r.type}:${r.symbol}`} onClick={() => openAsset(r.symbol, r.type)}
                      data-testid={`market-recent-${r.symbol}`}
                      className="px-3 py-1.5 rounded-full text-xs font-mono-tab uppercase bg-secondary/30 border border-border/40 hover:bg-secondary/60 transition-colors">
                {r.symbol.toString().toUpperCase()}
                <span className="ml-1 text-[9px] text-muted-foreground">{r.type}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
